import {
  CareerTrackCard,
  CareerTrackRadio,
  Tier,
  TrackCode,
  TRACK_CODE_TO_NAME,
  TIER_TO_CAREER_TRACK_NAME,
  CareerTrack,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { handleShareWithNavigator } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { CareerUserResponseSet } from '@backrs_ui/grow-ui/src/models/models';
import {
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  setNuggetToAdd,
  openPlaylistListStep,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { GROW_URL } from '@backrs_ui/grow-ui/src/settings';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';

import { CareerTrackIllustration } from './CareerTrackIllustration';
import { Header } from './Header';

const STRINGS = appStrings.modules.minimumTrack;

const CAREER_TRACK_CODES = Object.keys(TRACK_CODE_TO_NAME) as TrackCode[];

type CareerTrackData = CareerTrack & {
  displayName: Tier;
};

type MinimumTrackPageProps = Readonly<{
  userHasBlueprint: boolean;
  userResponseSet: CareerUserResponseSet | null | undefined;
  selectedTrack: Tier;
  careerTracks: CareerTrack[];
  isShared: boolean;
}>;

export function MinimumTrackPage({
  userHasBlueprint,
  userResponseSet,
  selectedTrack,
  careerTracks,
  isShared = false,
}: MinimumTrackPageProps) {
  const dispatch = useAppDispatch();
  const { data: bookmarks = [] } = useGetBookmarksQuery(undefined);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();

  const getSelectedTrackCode = (tier: Tier): TrackCode => {
    const trackCode = CAREER_TRACK_CODES.find((code) => TRACK_CODE_TO_NAME[code] === tier);

    if (!trackCode) {
      console.error('No track code found for tier:', tier);
    }

    return trackCode || 'semi-skilled';
  };

  const selectedCareerTrack =
    careerTracks.find((track) => track.code === getSelectedTrackCode(selectedTrack)) ||
    careerTracks[0];

  const selectedTrackDisplayName = TRACK_CODE_TO_NAME[selectedCareerTrack.code];
  const selectedTrackName = TIER_TO_CAREER_TRACK_NAME[selectedTrackDisplayName];

  const handleBookmark = async (careerTrackId: number) => {
    const bookmarkId = bookmarks?.find((b) => b.content_career_track?.id === careerTrackId)?.id;

    if (bookmarkId) {
      await deleteBookmark(bookmarkId);
    } else {
      await postBookmark({
        type: 'career-track',
        itemId: careerTrackId,
      });
    }
  };

  const handleShare = async (careerTrack: CareerTrack) => {
    await handleShareWithNavigator({
      title: `Career Track: ${TRACK_CODE_TO_NAME[careerTrack.code]}`,
      text: careerTrack.description || '',
      url: `${GROW_URL}/minimum-track`,
    });
  };

  const handleAddTask = (careerTrack: CareerTrack) => {
    const nugget = {
      id: careerTrack.id,
      slug: careerTrack.id.toString(),
      title: TRACK_CODE_TO_NAME[careerTrack.code],
      description: careerTrack.description || '',
      star_rating: 0,
      value_rating: 0,
      is_browsable: false,
      variant: 'career-track' as const,
    };

    dispatch(setNuggetToAdd(nugget));
    dispatch(openPlaylistListStep());
  };

  return (
    <Box sx={backgroundSx}>
      <Box sx={containerSx}>
        <Header
          userHasBlueprint={userHasBlueprint}
          userResponseSet={userResponseSet}
          isShared={isShared}
        />

        {/* Static Selected Track Section */}
        <Box sx={selectedTrackSectionContainerSx}>
          <Box sx={selectedTrackTierContainerSx}>
            <Box sx={selectedTrackTitleSx}>
              <CareerTrackRadio careerTrack={selectedTrackName} />
              <Typography variant="display-xs-semibold">
                {TRACK_CODE_TO_NAME[selectedCareerTrack.code]}
              </Typography>
            </Box>
            <Typography variant="text-md-regular">
              <ReactMarkdown>{selectedCareerTrack.description}</ReactMarkdown>
            </Typography>
          </Box>
          <Box sx={selectedTrackImgContainerSx}>
            <CareerTrackIllustration
              trackCode={selectedCareerTrack.code}
              alt={`${selectedTrack} career track illustration`}
            />
          </Box>
        </Box>

        {/* All Career Tracks Section */}
        <Box sx={allTracksSectionSx}>
          <Box sx={allTracksHeaderContainerSx}>
            <Typography variant="text-lg-semibold">{STRINGS.title}</Typography>
            <Typography
              color={(theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
              variant="text-sm-regular"
            >
              {STRINGS.description}
            </Typography>
          </Box>

          <Box sx={tracksContainerSx}>
            {careerTracks.map((careerTrack) => {
              const trackData: CareerTrackData = {
                ...careerTrack,
                displayName: TRACK_CODE_TO_NAME[careerTrack.code],
              };

              return (
                <CareerTrackCard
                  key={trackData.id}
                  code={trackData.code}
                  subtitle={STRINGS.careerTrack}
                  description={trackData.description || ''}
                  onBookmark={() => handleBookmark(trackData.id)}
                  onShare={() => handleShare(trackData)}
                  onAddTask={() => handleAddTask(trackData)}
                  hasBookmarked={bookmarks?.some(
                    (b) => b.content_career_track?.id === trackData.id,
                  )}
                  hideActions={isShared}
                />
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const backgroundSx: SxProps<Theme> = ({ backrs }) => ({
  minHeight: '100dvh',
  width: '100%',
  containerType: 'inline-size',
  mb: backrs.spacing['spacing-2xl'],
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
  width: '100%',
  '@media (max-width: 600px)': {
    gap: 0,
  },
});

const selectedTrackSectionContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'space-between',
  pt: backrs.spacing['spacing-2xl'],
  '@container (min-width: 900px)': {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: backrs.spacing['spacing-3xl'],
    pt: 0,
  },
});

const selectedTrackTierContainerSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

const selectedTrackTitleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  px: backrs.spacing['spacing-xxs'],
});

const selectedTrackImgContainerSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

const allTracksSectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  '@media (max-width: 600px)': {
    pt: backrs.spacing['spacing-4xl'],
  },
});

const allTracksHeaderContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  pb: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-3xl'] },
});

const tracksContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'start',
});
